import { useEffect, useRef } from 'react'
import { CalendarDays, GraduationCap, Backpack } from 'lucide-react'
import { useLocale } from '../i18n/LocaleContext'
import { getRegistrationPath } from '../utils/routes'
import { viewItem } from '../lib/ecommerce'
import type { CampData } from '../types/camp'
import Modal from './ui/Modal'
import Button from './ui/Button'
import PlaceholderImage from './ui/PlaceholderImage'
import AvailabilityBadge from './ui/AvailabilityBadge'

interface CampDetailModalProps {
  camp: CampData | null
  open: boolean
  onClose: () => void
}

export default function CampDetailModal({ camp, open, onClose }: CampDetailModalProps) {
  const { locale, t } = useLocale()

  // GA4 view_item — fire once each time the modal opens for a camp.
  const lastViewed = useRef<string | null>(null)
  useEffect(() => {
    if (open && camp && lastViewed.current !== camp.id) {
      lastViewed.current = camp.id
      viewItem(camp, locale)
    }
    if (!open) lastViewed.current = null
  }, [open, camp, locale])

  if (!camp) return null

  const title = camp.title[locale]
  const registrationPath = getRegistrationPath(locale)
  const isFull = !!camp.availability && camp.availability.available <= 0

  return (
    <Modal open={open} onClose={onClose} ariaLabel={title}>
      <div className="p-6 md:p-8">
        {/* Title + Availability */}
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-heading font-semibold text-secondary pr-8">{title}</h3>
          <AvailabilityBadge availability={camp.availability} locale={locale} />
        </div>

        {/* Date & Category badges */}
        <div className="flex flex-wrap gap-3 mt-4">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent text-primary text-sm font-medium rounded-full">
            <CalendarDays size={16} />
            {camp.dates[locale]}
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-surface text-text-secondary text-sm font-medium rounded-full border border-border">
            <GraduationCap size={16} />
            {camp.category[locale]}
          </span>
        </div>

        {/* Photo */}
        <div className="mt-6 px-2 md:px-8">
          {camp.image && !camp.image.includes('placeholder') ? (
            <img
              src={camp.image}
              alt={title}
              width={1280}
              height={720}
              className="w-full aspect-video object-cover rounded-btn"
              loading="lazy"
            />
          ) : (
            <PlaceholderImage
              label={title}
              variant="camp"
              className="w-full aspect-video rounded-btn"
            />
          )}
        </div>

        {/* Description */}
        <p className="mt-6 text-text-secondary leading-relaxed">
          {camp.shortDescription[locale]}
        </p>

        {/* Activities */}
        <div className="mt-6">
          <h4 className="font-heading font-medium text-base text-secondary mb-3">
            {t.modal.activities}
          </h4>
          <ul className="list-disc list-inside space-y-1 text-text-secondary text-sm">
            {camp.activities[locale].map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>

        {/* Packing List */}
        <div className="mt-6">
          <h4 className="font-heading font-medium text-base text-secondary mb-3 flex items-center gap-2">
            <Backpack size={18} className="text-primary" />
            {t.modal.packingList}
          </h4>
          <ul className="list-disc list-inside space-y-1 text-text-secondary text-sm">
            {camp.packingList[locale].map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>

        {/* Price */}
        <div className="mt-8 p-4 bg-accent rounded-btn text-center">
          <p className="text-sm text-text-secondary mb-1">{t.modal.price}</p>
          <p className="text-2xl font-heading font-semibold text-primary">{camp.price}</p>
        </div>

        {/* CTA */}
        <div className="mt-6 text-center">
          {isFull ? (
            <Button size="lg" disabled>
              {locale === 'hu' ? 'Betelt' : 'Complet'}
            </Button>
          ) : (
            <Button
              to={`${registrationPath}?camp=${camp.id}`}
              size="lg"
              onClick={onClose}
            >
              {t.modal.register}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
